import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { createUseStyles } from 'react-jss';
import { splitGuide, GuidePart } from 'shared/lib/aiGuide/guideParts';
import Button from '~/app/ui/Button';
import StatusBadge from '~/app/ui/StatusBadge';
import type { Tone } from '~/app/ui/tokens';
import { S, A } from '~/app/ui/tokens';

// Where each part comes from (see shared/lib/aiGuide/guideParts and compose.ts).
const PARTS: Record<GuidePart, { label: string; tone: Tone; hint: string } | null> = {
  about: null,
  'built-in': { label: 'Built in', tone: 'gray', hint: 'Comes with Genesis and always applies. It can’t be edited.' },
  guide: { label: 'Guide text', tone: 'blue', hint: 'The part super admins write, under “Edit the guide”.' },
  generated: { label: 'From Genesis', tone: 'green', hint: 'Written by Genesis for whoever reads it: their access, categories, templates, screens and the code rules. It updates by itself.' }
};

const useStyles = createUseStyles({
  legend: { display: 'flex', flexWrap: 'wrap', gap: '8px 18px', alignItems: 'center', margin: '0 0 12px', fontSize: 13, color: S.muted },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 8 },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' },
  count: { fontSize: 13, color: S.muted },
  list: { border: `1px solid ${S.line}`, borderRadius: A.radius, background: A.surface, overflow: 'hidden' },
  item: {
    borderTop: `1px solid ${S.line}`,
    '&:first-child': { borderTop: 0 },
    '& > summary': {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '11px 16px',
      cursor: 'pointer',
      listStyle: 'none',
      fontSize: 14,
      fontWeight: 600,
      color: S.fg,
      '&::-webkit-details-marker': { display: 'none' },
      '&:hover': { background: S.chromeHover },
      '&:focus-visible': { outline: `2px solid ${S.fg}`, outlineOffset: -2 }
    },
    '&[open] > summary': { borderBottom: `1px solid ${S.line}` },
    '&[open] $chevron': { transform: 'rotate(90deg)' }
  },
  chevron: { fontSize: '18px !important', color: S.muted, transition: 'transform 120ms ease' },
  heading: { flex: 1, minWidth: 0 },
  // The guide as a readable document (AI assistants read the plain text).
  body: {
    padding: '6px 20px 16px 44px',
    fontSize: 14,
    lineHeight: 1.6,
    color: S.fg,
    maxWidth: 900,
    '& p, & ul, & ol, & blockquote': { margin: '10px 0' },
    '& ul, & ol': { paddingLeft: 22 },
    '& li': { margin: '4px 0' },
    '& h3, & h4': { margin: '16px 0 6px', fontSize: 14, fontWeight: 700 },
    '& blockquote': { padding: '2px 12px', borderLeft: `3px solid ${S.line}`, color: S.muted },
    '& code': { fontFamily: S.mono, fontSize: 12.5, padding: '1px 4px', borderRadius: 4, background: S.chrome },
    '& pre': { margin: '10px 0', padding: '12px 14px', maxHeight: 420, overflow: 'auto', borderRadius: A.radius, background: S.chrome, border: `1px solid ${S.line}` },
    '& pre code': { padding: 0, background: 'none', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
    '& a': { color: S.fg }
  },
  tableWrap: { margin: '10px 0', overflowX: 'auto' },
  table: {
    borderCollapse: 'collapse',
    fontSize: 13,
    '& th, & td': { padding: '6px 10px', border: `1px solid ${S.line}`, textAlign: 'left', verticalAlign: 'top' },
    '& th': { background: S.chrome, fontWeight: 600 }
  }
});

type Block = { kind: 'markdown' | 'table'; lines: string[] };

// Tables are drawn here (react-markdown needs a plugin Genesis doesn't ship for them); everything else is
// Markdown. Lines inside code blocks stay code, whatever they start with.
const toBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  let fence: string | null = null;
  text.split('\n').forEach((line) => {
    const opener = line.match(/^(`{3,}|~{3,})/);
    if (fence) {
      if (opener && line.trim() === opener[1] && opener[1][0] === fence[0] && opener[1].length >= fence.length) fence = null;
    } else if (opener) {
      fence = opener[1];
    }
    const kind = !fence && !opener && /^\s*\|/.test(line) ? 'table' : 'markdown';
    const last = blocks[blocks.length - 1];
    if (last && last.kind === kind) last.lines.push(line);
    else blocks.push({ kind, lines: [line] });
  });
  return blocks;
};

const cellsOf = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
const Inline: React.FC<{ text: string }> = ({ text }) => (
  <ReactMarkdown components={{ p: ({ children }) => <>{children}</> }}>{text}</ReactMarkdown>
);

const Table: React.FC<{ lines: string[]; className: string; wrapClassName: string }> = ({ lines, className, wrapClassName }) => {
  const rows = lines.map(cellsOf).filter(cells => !cells.every(cell => /^:?-{3,}:?$/.test(cell)));
  const [head, ...rest] = rows;
  if (!head) return null;
  return (
    <div className={wrapClassName}>
      <table className={className}>
        <thead><tr>{head.map((cell, i) => <th key={i}><Inline text={cell} /></th>)}</tr></thead>
        <tbody>{rest.map((cells, r) => <tr key={r}>{cells.map((cell, i) => <td key={i}><Inline text={cell} /></td>)}</tr>)}</tbody>
      </table>
    </div>
  );
};

/**
 * The guide for people to read: its sections, closed except the first, each marked with where it comes from.
 * Built only from the text AI assistants read, so the two can't differ.
 */
const GuideDocument: React.FC<{ text: string }> = ({ text }) => {
  const c = useStyles();
  const sections = useMemo(() => splitGuide(text), [text]);
  const [open, setOpen] = useState<Record<number, boolean>>({ 0: true });
  const allOpen = sections.length > 0 && sections.every((_, i) => open[i]);
  const setAll = (value: boolean) => setOpen(sections.reduce<Record<number, boolean>>((all, _, i) => ({ ...all, [i]: value }), {}));

  return (
    <div data-testid="ai-guide-document">
      <div className={c.legend}>
        {(Object.keys(PARTS) as GuidePart[]).map((part) => {
          const p = PARTS[part];
          return p && (
            <span key={part} className={c.legendItem} title={p.hint}>
              <StatusBadge tone={p.tone} label={p.label} />
              {p.hint}
            </span>
          );
        })}
      </div>
      <div className={c.toolbar}>
        <span className={c.count}>{sections.length} sections</span>
        <Button size="sm" onClick={() => setAll(!allOpen)} data-testid="ai-guide-toggle-all">{allOpen ? 'Collapse all' : 'Expand all'}</Button>
      </div>
      <div className={c.list}>
        {sections.map((s, i) => {
          const p = PARTS[s.part];
          return (
            <details
              key={`${i}-${s.heading}`}
              className={c.item}
              open={!!open[i]}
              onToggle={(e) => { const value = (e.currentTarget as HTMLDetailsElement).open; setOpen(prev => (prev[i] === value ? prev : { ...prev, [i]: value })); }}
              data-part={s.part}
            >
              <summary>
                <i className={`material-icons ${c.chevron}`} aria-hidden>chevron_right</i>
                <span className={c.heading}>{s.heading}</span>
                {p && <StatusBadge tone={p.tone} label={p.label} title={p.hint} />}
              </summary>
              {/* Drawn only when open: twenty sections of Markdown aren't needed to show their names. */}
              {open[i] && (
                <div className={c.body}>
                  {toBlocks(s.text).map((block, b) => (block.kind === 'table'
                    ? <Table key={b} lines={block.lines} className={c.table} wrapClassName={c.tableWrap} />
                    : <ReactMarkdown key={b}>{block.lines.join('\n')}</ReactMarkdown>))}
                </div>
              )}
            </details>
          );
        })}
      </div>
    </div>
  );
};

export default GuideDocument;
